<script lang="ts">
  import { onMount } from "svelte";

  import { fetchAndSetConfigurationContext } from "$lib/elev8Api/configurationContext.svelte";
  import { primeContentFromCache } from "$lib/elev8Api/contentFetch";
  import {
    elev8Handshake,
    type HandshakeStatus,
  } from "$lib/elev8Api/handshake";
  import { awaitStartupJitter } from "$lib/elev8Api/startupJitter";
  import { initializeErrorReporter } from "$lib/errorHandling/errorReporter";
  import { parseElev8Url } from "$lib/utils/urlUtils";
  import { isPreviewMode } from "$lib/utils/previewMode";

  import AppsignalLogger from "$lib/utils/AppsignalLogger.svelte";
  import DataPipeline from "$lib/dataPipeline/DataPipeline.svelte";
  import ErrorBoundary from "$lib/errorHandling/ErrorBoundary.svelte";
  import LoadingContent from "$lib/content/components/LoadingContent.svelte";
  import Pop from "$lib/proofOfPlay/Pop.svelte";
  import ServiceWorkerUpdater from "$lib/ServiceWorkerUpdater.svelte";
  import SpotController from "$lib/spot/SpotController.svelte";
  import Telemetry from "$lib/telemetry/Telemetry.svelte";

  let displayId = $state("");
  let primed = $state(false);
  let handshakeStatus: HandshakeStatus | undefined = $state();
  let devOverlay = $state(false);
  let remoteLog: boolean | undefined = $state();
  const config = fetchAndSetConfigurationContext(() =>
    handshakeStatus === "found" ? displayId : ""
  );

  onMount(async () => {
    ({ displayId, devOverlay, remoteLog } = parseElev8Url(
      window.location.href
    ));
    initializeErrorReporter(displayId);

    await primeContentFromCache(displayId);
    primed = true;

    await awaitStartupJitter();
    ({ status: handshakeStatus } = await elev8Handshake(
      displayId,
      window.innerWidth,
      window.innerHeight
    ));
  });

  let showContent = $derived(
    primed && !!displayId && handshakeStatus !== "not_found"
  );

  let remoteLogEnabled = $derived(
    config.settings.debugging_enabled ||
      (remoteLog ?? config.settings.remote_logging_enabled)
  );
</script>

<ErrorBoundary>
  <main class="h-full w-full overflow-hidden">
    {#if showContent}
      {#if handshakeStatus === "found" && config.settings.telemetry_enabled}
        <Telemetry {displayId} />
      {/if}
      {#if remoteLogEnabled}
        <AppsignalLogger {displayId} />
      {/if}
      <SpotController {displayId} {devOverlay} />
      <DataPipeline {displayId} />
      <Pop />
    {:else if !displayId}
      <article class="h-full flex flex-col justify-center p-8">
        <p class="text-xl my-2">
          Display ID is missing. URL should be formatted:
        </p>
        <code class="bg-gray-200 p-4 mx-2">
          "https://elev8-anywhere-v2.verticalcity.com/display/some-actual-displayId"</code
        >
      </article>
    {:else}
      <LoadingContent
        {displayId}
        msg={handshakeStatus === "not_found" ? "Display ID Invalid" : ""}
      />
    {/if}
  </main>

  {#if isPreviewMode()}
    <div
      class="absolute top-0 right-0 bg-red-600 text-white text-4xl font-bold py-2 px-32 rotate-45 translate-x-28 translate-y-14 shadow-lg z-[99]"
    >
      PREVIEW
    </div>
  {/if}

  <ServiceWorkerUpdater />
</ErrorBoundary>
